import React, { useMemo } from 'react';
import { PlayIcon } from 'lucide-react';
import { BeheerLayout } from '../../components/beheer/BeheerLayout';
import { BeheerSection } from '../../components/beheer/BeheerSection';
import { ServiceHealth } from '../../components/beheer/systeem/ServiceHealth';
import { BusMonitor } from '../../components/beheer/systeem/BusMonitor';
import { ScenarioTests } from '../../components/beheer/systeem/ScenarioTests';
import { Backups } from '../../components/beheer/systeem/Backups';
import { NetworkZones } from '../../components/beheer/systeem/NetworkZones';
import { services as seedServices } from '../../data/beheer/system';
import { useDb } from '../../hooks/useDb';
import { useScenarioRunner } from '../../hooks/useScenarioRunner';
import type { Service, ServiceStatus } from '../../types/beheer';
const GPU_STATUS = {
  slaapt: {
    status: 'slaapt',
    label: 'slaapt'
  },
  'wordt gewekt': {
    status: 'traag',
    label: 'wordt gewekt'
  },
  actief: {
    status: 'gezond',
    label: 'actief'
  }
} as const;
export function Systeem() {
  const meta = useDb(t => t.meta);
  const {
    runs,
    run,
    runAll
  } = useScenarioRunner();
  const anyRunning = Object.values(runs).some(r => r.running);

  // Live overrides from the house state: GPU-brein and the Claude API follow meta.
  const services = useMemo<(Service & {
    statusLabel?: string;
  })[]>(() => seedServices.map(s => {
    if (s.id === 'gpu') {
      const g = GPU_STATUS[meta.gpu];
      return {
        ...s,
        status: g.status,
        statusLabel: g.label,
        latencyMs: meta.gpu === 'actief' ? 34 : null
      };
    }
    if (s.id === 'claude') {
      if (!meta.connections.internet || !meta.connections.cloud) return {
        ...s,
        status: 'storing',
        statusLabel: 'onbereikbaar',
        latencyMs: null
      };
      if (meta.cloud.localOnly) return {
        ...s,
        status: 'slaapt',
        statusLabel: 'gepauzeerd (budget)'
      };
    }
    return s;
  }), [meta]);
  const tally = services.reduce<Record<ServiceStatus, number>>((acc, s) => ({
    ...acc,
    [s.status]: acc[s.status] + 1
  }), {
    gezond: 0,
    traag: 0,
    slaapt: 0,
    storing: 0
  });
  return <BeheerLayout title="Systeem" intro="De machinekamer: diensten, berichtenbus, scenariotests, back-ups en netwerk." aside={<p className="text-[14px] text-ink-soft" data-id="element-1917" data-mp="207y7amuwk2wl">
          <span className="text-ink" data-id="element-1918" data-mp="207594aa0elq2">{tally.gezond} gezond</span>
          {tally.traag > 0 && <span className="text-amber" data-id="element-1919" data-mp="1v9wnfzpnjzk2"> · {tally.traag} traag</span>}
          {tally.slaapt > 0 && <span className="text-ink-muted" data-id="element-1920" data-mp="1q9hbncl0r047"> · {tally.slaapt} slaapt</span>}
          {tally.storing > 0 && <span className="text-alarm" data-id="element-1921" data-mp="1m7dmjl5ffx0g"> · {tally.storing} storing</span>}
        </p>} data-id="element-1916" data-mp="29z159fsjc1rn">
      <BeheerSection id="diensten" title="Diensten" description="Alles draait op je eigen server, behalve de Claude API." className="mt-10" data-id="element-1922" data-mp="1h9ltuowyk6v7">
        <ServiceHealth services={services} data-id="element-1923" data-mp="3glewp26fg6fu" />
      </BeheerSection>

      <BeheerSection id="bus" title="Busmonitor" description="Live MQTT-verkeer per topicboom." className="mt-16" data-id="element-1924" data-mp="3bmts6qk570ll">
        <BusMonitor data-id="element-1925" data-mp="3j5tep52wh896" />
      </BeheerSection>

      <BeheerSection id="scenarios" title="Scenariotests" description="Elke avond om 21:00 automatisch. Een run schrijft een rij in het logboek." className="mt-16" action={<button type="button" onClick={runAll} disabled={anyRunning} className="inline-flex h-10 items-center gap-2 whitespace-nowrap rounded-full bg-ink px-4 text-[14px] font-semibold text-night-950 transition-colors duration-150 hover:bg-white disabled:opacity-40" data-id="element-1927" data-mp="1pv0j7t6gcr5">
            <PlayIcon className="h-4 w-4" aria-hidden data-id="element-1928" data-mp="3a4vaalojuyq2" /> Alles draaien
          </button>} data-id="element-1926" data-mp="3o5pib37oqxo7">
        <ScenarioTests runs={runs} onRun={run} data-id="element-1929" data-mp="3e8b316xi0exh" />
      </BeheerSection>

      <div className="mt-16 grid gap-10 xl:grid-cols-2" data-id="element-1930" data-mp="pg8l6d9fn9bp">
        <BeheerSection id="backups" title="Back-ups" description="Versleuteld naar de NAS, nachtelijk ook offsite." data-id="element-1931" data-mp="phrypw3pf69s">
          <Backups data-id="element-1932" data-mp="s057plr2i0y6" />
        </BeheerSection>
        <BeheerSection id="netwerk" title="Netwerkzones" description="Apparaten zitten gescheiden; IoT en camera’s kunnen niet naar buiten." data-id="element-1933" data-mp="s16ma07al5kn">
          <NetworkZones data-id="element-1934" data-mp="n3ermyf9j5cr" />
        </BeheerSection>
      </div>
    </BeheerLayout>;
}